/**
 * SupportContext - Standardized context for Support AI
 * Provides consistent context object across all entry points
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useTenant } from './TenantContext';

// Role options for the role selector
export const SUPPORT_ROLES = [
  { key: 'pm', label: 'Project Manager', description: 'Day-to-day project tracking' },
  { key: 'product_owner', label: 'Product Owner', description: 'Scope discovery & structuring', advisoryFocus: 'discovery → scope structuring → decisions linkage → candidate baseline → doc sign-offs' },
  { key: 'systems_admin', label: 'Systems Admin', description: 'Technical setup & config' },
  { key: 'product_admin', label: 'Product Admin', description: 'Product configuration' },
  { key: 'pmo', label: 'PMO', description: 'Cross-project oversight' },
  { key: 'executive', label: 'Executive', description: 'Strategic view' },
  { key: 'guest', label: 'Guest', description: 'Limited access' },
];

const SupportContextValue = createContext(null);

export const SupportContextProvider = ({ children }) => {
  const location = useLocation();
  const { user } = useAuth();
  const { tenantData } = useTenant();
  
  // State for drawer visibility
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('advisory'); // 'advisory' | 'product'
  const [initialMessage, setInitialMessage] = useState('');
  
  // Entity context (set when opening from Inspector or specific items)
  const [entityContext, setEntityContext] = useState(null);
  
  // Last upload error (set by Upload Center)
  const [lastUploadError, setLastUploadError] = useState(null);
  
  // Active filters (set by filter components)
  const [activeFilters, setActiveFilters] = useState({});
  
  // User's preferred role (loaded from API)
  const [preferredRole, setPreferredRole] = useState(null);
  
  // Selected project
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  
  // Build the SupportContext object
  const buildSupportContext = useCallback((overrides = {}) => {
    const tenantTier = tenantData?.subscription?.tier || 'Business';
    const userRole = preferredRole || user?.role || 'guest';
    
    return {
      page: location.pathname,
      userRole,
      tenantTier: tenantTier.charAt(0).toUpperCase() + tenantTier.slice(1),
      projectId: selectedProjectId || entityContext?.projectId || null,
      entityType: entityContext?.entityType || overrides.entityType || null,
      entityId: entityContext?.entityId || overrides.entityId || null,
      baselineStatus: entityContext?.baselineStatus || null,
      lastUploadError: lastUploadError,
      filters: Object.keys(activeFilters).length > 0 ? activeFilters : null,
      ...overrides
    };
  }, [location.pathname, user, tenantData, preferredRole, selectedProjectId, entityContext, lastUploadError, activeFilters]);
  
  // Open drawer with optional context
  const openSupportDrawer = useCallback((options = {}) => {
    const { mode = 'advisory', message = '', entity = null } = options;
    
    setDrawerMode(mode);
    setInitialMessage(message);
    
    if (entity) {
      setEntityContext({
        entityType: entity.type,
        entityId: entity.id,
        projectId: entity.projectId,
        baselineStatus: entity.baselineStatus,
        parentChain: entity.parentChain
      });
    }
    
    setIsDrawerOpen(true);
  }, []);
  
  // Close drawer
  const closeSupportDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setInitialMessage('');
    // Keep entity context for a bit in case user reopens
    setTimeout(() => {
      if (!isDrawerOpen) {
        setEntityContext(null);
      }
    }, 5000);
  }, [isDrawerOpen]);
  
  // Set entity context (for Inspector integration)
  const setInspectorContext = useCallback((entity) => {
    setEntityContext({
      entityType: entity?.type,
      entityId: entity?.id,
      projectId: entity?.project_id || entity?.projectId,
      baselineStatus: entity?.baselineStatus,
      parentChain: entity?.parentChain
    });
  }, []);
  
  // Context value
  const value = useMemo(() => ({
    // Drawer state
    isDrawerOpen,
    drawerMode,
    initialMessage,
    openSupportDrawer,
    closeSupportDrawer,
    
    // Context building
    buildSupportContext,
    supportContext: buildSupportContext(),
    
    // Entity context
    entityContext,
    setEntityContext,
    setInspectorContext,
    
    // Upload error tracking
    lastUploadError,
    setLastUploadError,
    
    // Filters
    activeFilters,
    setActiveFilters,
    
    // User preferences
    preferredRole,
    setPreferredRole,
    selectedProjectId,
    setSelectedProjectId,
    
    // Role options
    SUPPORT_ROLES,
  }), [
    isDrawerOpen, drawerMode, initialMessage, openSupportDrawer, closeSupportDrawer,
    buildSupportContext, entityContext, setInspectorContext,
    lastUploadError, activeFilters, preferredRole, selectedProjectId
  ]);
  
  return (
    <SupportContextValue.Provider value={value}>
      {children}
    </SupportContextValue.Provider>
  );
};

export const useSupportContext = () => {
  const context = useContext(SupportContextValue);
  if (!context) {
    throw new Error('useSupportContext must be used within SupportContextProvider');
  }
  return context;
};

export default SupportContextProvider;
